import * as React from 'react';
import { IgcNavDrawerComponent as Component } from 'igniteui-webcomponents';
import type { IgcNavDrawerComponentEventMap } from 'igniteui-webcomponents';
import { type EventName, createComponent } from '../react-props.js';

/**
 * A side navigation container that provides
 * quick access between views within an application.
 *
 * For non-relative positions (`start`, `end`, `top`, `bottom`) the drawer is
 * rendered as a native `<dialog>` element, providing modal semantics, automatic
 * focus trapping, and a backdrop. For the `relative` position it is rendered
 * inline as a `<nav>` landmark.
 *
 * When content is provided in the `mini` slot, a compact icon-only variant is
 * always displayed alongside the main drawer (hidden only while the full drawer
 * is open).
 *
 * The component integrates with the
 * [Invoker Commands API](https://developer.mozilla.org/en-US/docs/Web/API/Invoker_Commands_API):
 * an Ignite button or a native `<button>` with `command="--show"` / `"--hide"` / `"--toggle"`
 * and `commandfor` pointing to this element will call the corresponding method
 * declaratively without any JavaScript.
 * @slot  - Renders the main navigation content of the drawer.
 * @slot mini - Renders the compact mini variant of the drawer.
 * @fires igcClosing - Emitted just before the drawer is closed by a user interaction. Cancelable — call `event.preventDefault()` to abort the closing sequence.
 * @fires igcClosed - Emitted just after the drawer is closed by a user interaction.
 * @csspart base - The base wrapper of the drawer.
 * @csspart main - The main content container of the drawer.
 * @csspart mini - The mini variant container of the drawer.
 * @class
 */
export const IgrNavDrawer = /* @__PURE__ */ createComponent({
  tagName: 'igc-nav-drawer',
  displayName: 'IgrNavDrawer',
  elementClass: Component,
  react: React,
  events: {
    onClosing: 'igcClosing' as EventName<
      IgcNavDrawerComponentEventMap['igcClosing']
    >,
    onClosed: 'igcClosed' as EventName<
      IgcNavDrawerComponentEventMap['igcClosed']
    >,
  },
});

export type IgrNavDrawer = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrNavDrawerModule = Component;
